import { PHISHING_USER_STATUS } from '../../domain/domainCodes.js'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowRight,
  FileBarChart,
  MailOpen,
  MousePointerClick,
  Send,
  ShieldCheck,
  TriangleAlert,
} from 'lucide-react'
import Button from '../../components/Button'
import { useOnChange, useRemembered } from '../../components/listMemory'
import { usePageParam } from '../../components/useListParams'
import Card from '../../components/Card'
import DataTable from '../../components/DataTable'
import ExportMenu from '../../components/ExportMenu'
import FilterBar from '../../components/FilterBar'
import Meter from '../../components/Meter'
import PageHeader from '../../components/PageHeader'
import useGoBack from '../../components/useGoBack'
import PageLoader from '../../components/PageLoader'
import StatStrip from '../../components/StatStrip'
import StatusBadge from '../../components/StatusBadge'
import { useToast } from '../../components/toastContext'
import {
  exportReport,
  getCampaignReport,
  getPhishingReport,
  listReportUsers,
} from './phishingService'
import { reportStrings as S } from './strings'
import './phishing.css'

/**
 * The funnel, in the order a recipient passes through it. The tones are the campaigns list's,
 * kept identical so the same outcome never wears two colours in one product: submitting
 * credentials is the WORST outcome and reporting the phish is the best.
 */
const STAGES = [
  { id: 'sent', icon: Send, tone: 'neutral' },
  { id: 'opened', icon: MailOpen, tone: 'info' },
  { id: 'clicked', icon: MousePointerClick, tone: 'warning' },
  { id: 'submitted', icon: TriangleAlert, tone: 'danger' },
  { id: 'reported', icon: ShieldCheck, tone: 'success' },
]

/** `PhishingUserStatus` — the tone each recipient state carries. */
const STATUS_TONE = {
  '-1': 'dark',
  [PHISHING_USER_STATUS.SENT]: 'dark',
  [PHISHING_USER_STATUS.EMAIL_OPENED]: 'info',
  [PHISHING_USER_STATUS.LINK_CLICKED]: 'warning',
  [PHISHING_USER_STATUS.DATA_SUBMITTED]: 'danger',
  [PHISHING_USER_STATUS.USER_REPORTED]: 'success',
}

/**
 * تقارير الإصطياد — the legacy `/PhishingReport`, both of its levels.
 *
 * BUILT, not wired: the two Razor pages it replaces could never render (their view paths
 * omitted the `Phishing/` folder, so every request threw), and there was no React screen at
 * all. One component serves both levels, because they are the same report at two scopes —
 * without `:campaignId` it is the whole programme, with it one campaign plus its recipients.
 *
 * WHAT THE FIVE FIGURES ACTUALLY COUNT is not what their names suggest, and the screen says
 * so in a footnote rather than letting a reader assume: `sent` is the number of recipients
 * enrolled, including any still scheduled; `opened` is cumulative; `clicked` excludes anyone
 * who went on to report it; and `reported` is driven by a date column rather than a status.
 */
export default function PhishingReportPage() {
  const { campaignId } = useParams()
  const navigate = useNavigate()
  /* Only the per-campaign view has an origin; the whole-programme report is a
     menu page and takes no arrow. */
  const goBack = useGoBack('/phishing/campaigns')
  const toast = useToast()

  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [usersLoading, setUsersLoading] = useState(false)
  /* The recipients' own search and page size, kept per CAMPAIGN — a search for one
     campaign's recipient means nothing in another's. The page below is in the address, so
     losing these two left `?page=3` pointing at page 3 of a different, unsearched list;
     the by-campaign table further down needs none of this, since it has no `query` prop
     and DataTable remembers its own box. */
  const [query, setQuery] = useRemembered(campaignId && `phishing-report:${campaignId}:query`, '')
  /* In the address; the campaign is already part of it. */
  const [page, setPage] = usePageParam()
  const [pageSize, setPageSize] = useRemembered(
    campaignId && `phishing-report:${campaignId}:pageSize`,
    25,
  )

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    const load = campaignId ? getCampaignReport(campaignId) : getPhishingReport()
    load
      .then((loaded) => {
        if (cancelled) return
        setReport(loaded)
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setReport(null)
        setLoading(false)
        toast.error(error?.message || S.actionFailed)
      })
    return () => {
      cancelled = true
    }
  }, [campaignId, toast])

  /* A new search is a new list, so it starts at its first page — on a CHANGE only, or the
     page carried in the address would be thrown away on arrival. */
  useOnChange(() => setPage(1), [query])

  /* The recipients, only at campaign scope. `campaignId` is never omitted: without it the
     endpoint answers with recipients from EVERY campaign rather than refusing. */
  useEffect(() => {
    if (!campaignId) return undefined
    let cancelled = false
    setUsersLoading(true)
    listReportUsers({ campaignId, page, pageSize, query })
      .then((response) => {
        if (cancelled) return
        setRows(response.rows)
        setTotal(response.total)
        setUsersLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setRows([])
        setTotal(0)
        setUsersLoading(false)
        toast.error(error?.message || S.actionFailed)
      })
    return () => {
      cancelled = true
    }
  }, [campaignId, page, pageSize, query, toast])

  const runExport = useCallback(async () => {
    const outcome = await exportReport(campaignId, query)
    if (!outcome.ok) toast.error(outcome.message || S.actionFailed)
  }, [campaignId, query, toast])

  const campaignColumns = useMemo(
    () => [
      {
        key: 'name',
        header: S.columns.campaign,
        render: (row) => <bdi>{row.name}</bdi>,
      },
      {
        key: 'startedOn',
        header: S.columns.startedOn,
        render: (row) => (
          <span className="ud__date" dir="ltr">
            {row.startedOn}
          </span>
        ),
      },
      {
        key: 'status',
        header: S.columns.status,
        // Localized server-side on this payload, unlike the recipient rows.
        render: (row) => <StatusBadge tone="dark">{row.status}</StatusBadge>,
      },
      ...STAGES.map((stage) => ({
        key: stage.id,
        header: S.stages[stage.id],
        align: 'center',
        value: (row) => row.totals[stage.id],
        render: (row) => {
          const count = row.totals[stage.id]
          if (stage.id === 'submitted') {
            return <span className={count > 0 ? 'ph__bad' : 'ph__nil'}>{count}</span>
          }
          if (stage.id === 'reported') {
            return <span className={count > 0 ? 'ph__good' : 'ph__nil'}>{count}</span>
          }
          return count
        },
      })),
      {
        key: 'open',
        header: <FileBarChart size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '72px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <Button variant="ghost" onClick={() => navigate(`/phishing/report/${row.id}`)}>
            <ArrowRight size={16} strokeWidth={1.75} />
          </Button>
        ),
      },
    ],
    [navigate],
  )

  const userColumns = useMemo(
    () => [
      { key: 'name', header: S.columns.user, render: (row) => <bdi>{row.name || row.username}</bdi> },
      { key: 'email', header: S.columns.email, render: (row) => <span dir="ltr">{row.email}</span> },
      { key: 'department', header: S.columns.department, render: (row) => <bdi>{row.department}</bdi> },
      {
        key: 'status',
        header: S.columns.status,
        value: (row) => S.states[row.status] ?? '',
        /* THE REPORTING DATE WINS. A recipient who reported the message is shown as having
           reported it whatever their status number says — the same rule the legacy applies,
           and the reason the two can disagree is that `reported` is tracked by date. */
        render: (row) => {
          const state = row.reportedOn ? 4 : row.status
          return (
            <StatusBadge tone={STATUS_TONE[state] ?? 'dark'}>
              {S.states[state] ?? String(row.status)}
            </StatusBadge>
          )
        },
      },
      {
        key: 'phishedCount',
        header: S.columns.phished,
        align: 'center',
        render: (row) => (
          <span className={row.phishedCount > 0 ? 'ph__bad' : 'ph__nil'}>{row.phishedCount}</span>
        ),
      },
      {
        key: 'reportedOn',
        header: S.columns.reportedOn,
        render: (row) =>
          row.reportedOn ? (
            <span className="ud__date" dir="ltr">
              {row.reportedOn}
            </span>
          ) : null,
      },
    ],
    [],
  )

  if (loading) {
    return (
      <>
        <PageHeader title={S.title} subtitle={S.subtitle} />
        <PageLoader />
      </>
    )
  }

  if (!report) return <PageHeader title={S.title} subtitle={S.subtitle} />

  const totals = report.totals
  const base = totals.sent || 0

  return (
    <>
      <PageHeader
        onBack={campaignId ? goBack : undefined}
        title={campaignId ? report.name : S.title}
        subtitle={campaignId ? `${S.subtitle} · ${report.startedOn || ''}` : S.subtitle}
        actions={
          campaignId ? (
            <Button variant="soft" onClick={() => navigate('/phishing/report')}>
              <ArrowRight size={16} strokeWidth={1.75} />
              {S.backToAll}
            </Button>
          ) : null
        }
      />

      <StatStrip
        label={S.funnel}
        items={STAGES.map((stage) => ({
          id: stage.id,
          icon: stage.icon,
          label: S.stages[stage.id],
          value: totals[stage.id],
          tone: stage.tone,
          selectable: false,
        }))}
      />

      {/* The same five figures as a share of the recipients, because a count alone does not
          say whether nine clicks is most of a campaign or a handful of it. */}
      <Card title={S.share} subtitle={S.shareHint}>
        <ul className="ph__funnel">
          {STAGES.slice(1).map((stage) => {
            const count = totals[stage.id]
            const pct = base ? Math.round((count / base) * 1000) / 10 : 0
            return (
              <li key={stage.id} className="ph__funnel-row">
                <span className="ph__funnel-label">
                  <bdi>{S.stages[stage.id]}</bdi>
                </span>
                <Meter value={pct} tone={stage.tone} label={S.stages[stage.id]} />
                <span className="ph__funnel-value" dir="ltr">
                  {pct}% ({count})
                </span>
              </li>
            )
          })}
        </ul>
        <p className="ph__note">{S.countingNote}</p>
      </Card>

      {!campaignId && (
        <Card title={S.byCampaign}>
          <DataTable
            columns={campaignColumns}
            rows={report.campaigns}
            getRowId={(row) => row.id}
            emptyMessage={S.noCampaigns}
            initialSort={{ key: 'startedOn', dir: 'desc' }}
          />
        </Card>
      )}

      {campaignId && (
        <>
          <FilterBar
            search={{ value: query, onChange: setQuery, placeholder: S.columns.user }}
            actions={<ExportMenu onExport={runExport} labels={{ excel: S.exportExcel }} />}
          />
          <DataTable
            /* A second list on the same screen: the address already belongs to the first. */
            pageParam={null}
            columns={userColumns}
            rows={rows}
            loading={usersLoading}
            query={query}
            getRowId={(row) => row.id}
            emptyMessage={S.noUsers}
            server={{
              page,
              pageSize,
              total,
              onChange: (next) => {
                setPage(next.page)
                setPageSize(next.pageSize)
              },
            }}
          />
        </>
      )}
    </>
  )
}
